import { ChangeEvent, memo, useCallback, useRef } from "react"

import { Authority, useAuthenticated } from "providers/A12n"
import {
  CellRendererProps,
  Table,
  useCell,
  useDatasource,
} from "components/Bable"
import { css, cx } from "@emotion/css"

import { ApprovalLevel } from "models/clientAgencyConf/profile"
import Button from "ui/buttons/Button"
import { ReactComponent as DeleteIcon } from "assets/delete.svg"
import { GhostMoneys } from "components/inputs/Moneys"
import GhostTextInput from "ui/inputs/GhostTextInput"
import IconButton from "ui/buttons/IconButton"
import WarningIcon from "@material-ui/icons/Error"
import { Tooltip } from "@material-ui/core"
import { WithAuthority } from "components/WithAuthority"
import { agencyClientStyles } from "./styles"
import { v1 as getUuid } from "uuid"
import { levelsState } from "./AgencyClientFlow"
import { useI18n } from "providers/I18n"
import { useRow } from "components/Bable/data/useRow"

type Props = {
  updateLevelRow: (level: ApprovalLevel, remove?: boolean) => void
  errors?: Record<string, Record<string, string>>
  approvalProcess: "THRESHOLD_LEVELS" | "MANUAL" | undefined
}

export const Levels = memo(function (props: Props) {
  const { t } = useI18n()
  const { updateLevelRow, errors, approvalProcess } = props
  const { hasAuthority } = useAuthenticated()

  const levelsSource = useDatasource({
    state: levelsState,
  })

  const onAddLevelRow = () => {
    levelsSource.state.appendRow(getEmptyLevelRow(levelsSource.itemCount))
  }

  const onDeleteRow = (id: string) => {
    levelsSource.state.removeRow(id)
  }

  const canAddMore = levelsSource.itemCount < 5

  const key = levelsSource.state.keys.value.join("_")

  return (
    <div
      className={cx(styles.wrapperThreshold, {
        [styles.wrapperManual]: approvalProcess === "MANUAL",
      })}
    >
      <Table<ApprovalLevel>
        key={key}
        columns={getColumns(
          t,
          updateLevelRow,
          onDeleteRow,
          hasAuthority,
          errors,
          approvalProcess,
        )}
        source={levelsSource}
        height={200}
        className={styles.table}
        //emptyClassName={cx("HeadlineH6Dark-greyLight", styles.addlevels)}
      />
      {canAddMore && hasAuthority(["AUTH_EDIT_FLOW"]) ? (
        <Button
          className={agencyClientStyles.textButton}
          color="primary"
          onClick={onAddLevelRow}
        >
          {approvalProcess !== "MANUAL"
            ? t("agency.client.approval.levels.add.row")
            : t("agency.client.approval.manual.levels.add.row")}
        </Button>
      ) : null}
    </div>
  )
})

const styles = {
  table: css`
    background-color: var(--white);
    .RebaseTable__header-cell {
      padding: 0 12px;
      font-size: 12px;
      color: var(--black);
      font-weight: bold;
      text-transform: capitalize;
    }
    .RebaseTable__grid-cell {
    }

    .RebaseTable__body-row:hover {
      background-color: var(--primary-light);
    }

    .RebaseTable__body-row--hovered {
      background-color: var(--primary-light);
    }

    .RebaseTable__cell {
      :last-child {
        display: flex;
        justify-content: center;
      }
    }
    .RebaseTable__cell:focus-within {
      box-shadow: none;
    }
  `,
  levels: css`
    margin-bottom: var(--spacing-m);
  `,
  wrapperManual: css`
    padding: var(--spacing-xxl) 0 0;
    width: 772px;
  `,
  wrapperThreshold: css`
    padding: var(--spacing-xxl) 0 0;
    width: 995px;
  `,
}

function getColumns(
  t: (key: string) => string,
  updateLevelRow: (level: ApprovalLevel) => void,
  onDeleteRow: (id: string) => void,
  hasAuthority: (allowed: Authority[]) => boolean,
  errors?: Record<string, Record<string, string>>,
  approvalProcess?: "THRESHOLD_LEVELS" | "MANUAL" | undefined,
) {
  const agency_columns = [
    {
      id: "actions",
      Header: t("actions"),
      dataKey: "actions",
      Cell: (props: CellRendererProps) => (
        <WithAuthority allowed={["AUTH_EDIT_FLOW"]}>
          <DeleteAction
            onDelete={onDeleteRow}
            updateLevelRow={updateLevelRow}
            {...props}
          />
        </WithAuthority>
      ),
    },
  ]

  let custom_columns = [] as any
  if (!hasAuthority(["AUTH_ACCESS_AGENCY_ADMIN"])) {
    custom_columns = agency_columns
  }

  let manual_columns = [
    {
      id: "approverFirstName",
      Header: t("agency.client.levels.FirstName"),
      dataKey: "approverFirstName",
      Cell: (props: CellRendererProps) => (
        <EditableCell
          updateLevelRow={updateLevelRow}
          errors={errors}
          {...props}
        />
      ),
    },
    {
      id: "approverLastName",
      Header: t("agency.client.levels.approverLastName"),
      dataKey: "approverLastName",
      Cell: (props: CellRendererProps) => (
        <EditableCell
          updateLevelRow={updateLevelRow}
          errors={errors}
          {...props}
        />
      ),
    },
    {
      id: "approverEmail",
      Header: t("agency.client.levels.Email"),
      dataKey: "approverEmail",
      Cell: (props: CellRendererProps) => (
        <EditableCell
          updateLevelRow={updateLevelRow}
          errors={errors}
          {...props}
        />
      ),
    },
    {
      id: "title",
      Header: t("agency.client.levels.title"),
      dataKey: "title",
      Cell: (props: CellRendererProps) => (
        <EditableCell
          updateLevelRow={updateLevelRow}
          errors={errors}
          {...props}
        />
      ),
    },
    ...custom_columns,
  ]

  let threshold_columns = [
    {
      id: "name",
      Header: t("agency.client.levels.level"),
      dataKey: "name",
      Cell: (props: CellRendererProps) => (
        <EditableCell
          updateLevelRow={updateLevelRow}
          errors={errors}
          {...props}
        />
      ),
    },
    {
      id: "amountThreshold",
      Header: t("agency.client.levels.threshold"),
      dataKey: "amountThreshold",
      Cell: (props: CellRendererProps) => {
        const { rowIndex } = props
        return rowIndex === 0 ? (
          <div style={{ padding: "0 0.5rem" }}>{0}</div>
        ) : (
          <EditableMoneyCell
            updateLevelRow={updateLevelRow}
            errors={errors}
            {...props}
          />
        )
      },
    },
    ...manual_columns,
  ]

  return approvalProcess === "MANUAL" ? manual_columns : threshold_columns
}

function DeleteAction(
  props: CellRendererProps & {
    onDelete: (id: string) => void
    updateLevelRow: (level: ApprovalLevel, remove?: boolean) => void
  },
) {
  const { rowId, onDelete, updateLevelRow } = props
  const row = useRow<ApprovalLevel>(rowId)

  const handleDelete = useCallback(() => {
    const level = row.get()
    updateLevelRow(level, true)
    onDelete(level.id)
  }, [onDelete, updateLevelRow, row])

  return (
    <IconButton
      className={agencyClientStyles.textButton}
      onClick={handleDelete}
    >
      <DeleteIcon />
    </IconButton>
  )
}

function EditableCell(
  props: CellRendererProps & {
    updateLevelRow: (level: ApprovalLevel) => void
    errors?: Record<string, Record<string, string>>
  },
) {
  const { t } = useI18n()
  const { hasAuthority } = useAuthenticated()
  const { dataKey, rowId, updateLevelRow, errors } = props
  const [value, setValue] = useCell<ApprovalLevel>(
    rowId,
    dataKey as keyof ApprovalLevel,
    "",
  )

  const error = errors?.[rowId]?.[dataKey]
  const row = useRow<ApprovalLevel>(rowId)
  let old = useRef(value)

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value)
  }

  const onBlur = () => {
    const hasValueDiff = old.current !== value
    if (dataKey !== undefined && hasValueDiff) {
      const level = row.get()
      updateLevelRow(level)
      old.current = value
    }
  }

  return (
    <>
      <Tooltip
        classes={{ tooltip: "BodyBody1Regular" }}
        title={value as string}
      >
        <GhostTextInput
          value={value as string}
          onChange={onChange}
          onBlur={onBlur}
          disabled={!hasAuthority(["AUTH_EDIT_FLOW"])}
        />
      </Tooltip>
      {error ? (
        <div
          className={css`
            position: absolute;
            top: 0;
            color: var(--red);
          `}
          title={t(error)}
        >
          <WarningIcon fontSize="small" color="inherit" />
        </div>
      ) : null}
    </>
  )
}

function EditableMoneyCell(
  props: CellRendererProps & {
    updateLevelRow: (level: ApprovalLevel) => void
    errors?: Record<string, Record<string, string>>
  },
) {
  const { dataKey, rowId, updateLevelRow, errors } = props
  const { hasAuthority } = useAuthenticated()
  const [value, setValue] = useCell<ApprovalLevel>(
    rowId,
    dataKey as keyof ApprovalLevel,
    "",
  )
  const error = errors?.[rowId]?.[dataKey]
  const row = useRow<ApprovalLevel>(rowId)
  let old = useRef(value)

  const onChange = (v: string) => {
    setValue(v)
  }

  const onBlur = () => {
    const hasValueDiff = old.current !== value
    if (dataKey !== undefined && hasValueDiff) {
      const level = row.get()
      updateLevelRow(level)
      old.current = value
    }
  }

  return (
    <GhostMoneys
      name={dataKey}
      error={!!error}
      helperText={error}
      value={value as string}
      setValue={onChange}
      onBlur={onBlur}
      disabled={!hasAuthority(["AUTH_EDIT_FLOW"])}
      hasTooltip
      classes={{
        input: css`
          font-size: 0.875rem;
          color: var(--black);
        `,
      }}
    />
  )
}

function getEmptyLevelRow(num: number): ApprovalLevel {
  return {
    id: getUuid(),
    name: `Level ${num + 1}`,
    title: "",
    amountThreshold: 0,
    approverFirstName: "",
    approverLastName: "",
    approverEmail: "",
  }
}
